import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Visibility } from '@mui/icons-material';
import { format } from 'date-fns';

import { useSupportChatNotifications } from '../../context/SupportChatNotificationContext';
import supportChatService from '../../services/supportChatService';
import CustomPagination from '../../components/common/CustomPagination';


const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Waiting', value: 'waiting' },
  { label: 'Active', value: 'active' },
  { label: 'Closed', value: 'closed' },
];

const RETENTION_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
  { label: 'Retention hold', value: 'hold' },
  { label: 'Purged', value: 'purged' },
  { label: 'All', value: '' },
];

const CLOSE_REASON_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Auto-closed (inactivity)', value: 'inactivity' },
];


const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch (error) {
    return value;
  }
};


const getDisplayName = (user) => {
  if (!user) {
    return '-';
  }
  return user.fullname || user.username || user.email || user.phone || `User ${user.id}`;
};


const getStatusColor = (status) => {
  if (status === 'active') return 'success';
  if (status === 'closed') return 'default';
  return 'warning';
};

const getSupportModeColor = (mode) => {
  if (mode === 'live_agent') return 'success';
  if (mode === 'waiting_agent') return 'warning';
  return 'info';
};

const formatSupportMode = (mode) => {
  if (!mode) {
    return '-';
  }
  return mode.replace(/_/g, ' ').toUpperCase();
};


export default function SupportChatList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    retention_status: 'active',
    close_reason: '',
  });
  const [draftFilters, setDraftFilters] = useState({
    status: '',
    search: '',
    retention_status: 'active',
    close_reason: '',
  });
  const [availability, setAvailability] = useState(null);
  const [availabilityDraft, setAvailabilityDraft] = useState({
    start_time: '09:00',
    end_time: '18:00',
    timezone: 'Africa/Lagos',
    is_enabled: true,
    manually_available: true,
  });
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilitySaving, setAvailabilitySaving] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const {
    lastSupportEvent,
    refreshUnreadCount,
    sessionUnreadCounts,
    socketConnected,
    unreadCount: unreadNotificationCount,
    setSoundAlertsEnabled,
    soundAlertsEnabled,
  } = useSupportChatNotifications();

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        page_size: pageSize,
      };

      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.retention_status) {
        params.retention_status = filters.retention_status;
      }
      if (filters.close_reason) {
        params.close_reason = filters.close_reason;
      }
      if (filters.search.trim()) {
        params.search = filters.search.trim();
      }

      const data = await supportChatService.listSessions(params);
      setSessions(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      console.error('Failed to fetch support chat sessions', err);
      setError(err?.response?.data?.detail || 'Failed to load support chat sessions.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const fetchAvailabilitySettings = useCallback(async () => {
    try {
      setAvailabilityLoading(true);
      const data = await supportChatService.getAvailabilitySettings();
      setAvailability(data);
      setAvailabilityDraft({
        start_time: String(data.start_time || '09:00').slice(0, 5),
        end_time: String(data.end_time || '18:00').slice(0, 5),
        timezone: data.timezone || 'Africa/Lagos',
        is_enabled: data.is_enabled !== false,
        manually_available: data.manually_available !== false,
      });
    } catch (err) {
      console.error('Failed to load support chat availability settings', err);
      setAvailabilityMessage('Unable to load live chat hours.');
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailabilitySettings();
  }, [fetchAvailabilitySettings]);

  const handleSaveAvailability = async () => {
    try {
      setAvailabilitySaving(true);
      setAvailabilityMessage('');
      const data = await supportChatService.updateAvailabilitySettings(availabilityDraft);
      setAvailability(data);
      setAvailabilityDraft({
        start_time: String(data.start_time || availabilityDraft.start_time || '09:00').slice(0, 5),
        end_time: String(data.end_time || availabilityDraft.end_time || '18:00').slice(0, 5),
        timezone: data.timezone || availabilityDraft.timezone || 'Africa/Lagos',
        is_enabled: data.is_enabled !== false,
        manually_available: data.manually_available !== false,
      });
      setAvailabilityMessage('Live chat availability updated.');
    } catch (err) {
      console.error('Failed to update support chat availability settings', err);
      setAvailabilityMessage(err?.response?.data?.detail || 'Unable to update live chat hours.');
    } finally {
      setAvailabilitySaving(false);
    }
  };

  useEffect(() => {
    if (!lastSupportEvent || lastSupportEvent.type !== 'support.notification.created') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      fetchSessions();
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [fetchSessions, lastSupportEvent]);

  const handleApplyFilters = () => {
    setPage(0);
    setFilters(draftFilters);
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(pageSize.toString());
      return;
    }
    setPageSize(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPageSize(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const waitingChatCount = useMemo(
    () => sessions.filter((session) => session.support_mode === 'waiting_agent').length,
    [sessions]
  );

  const displaySessions = useMemo(
    () => sessions.map((session) => ({
      ...session,
      unread_notification_count:
        sessionUnreadCounts[session.id] ?? session.unread_notification_count ?? 0,
    })),
    [sessionUnreadCounts, sessions]
  );

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Session ID',
        minWidth: 240,
        flex: 1.2,
      },
      {
        field: 'user',
        headerName: 'User',
        minWidth: 180,
        flex: 1,
        renderCell: ({ value }) => getDisplayName(value),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 120,
        flex: 0.7,
        renderCell: ({ value }) => (
          <Chip
            label={(value || '').toUpperCase()}
            color={getStatusColor(value)}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        ),
      },
      {
        field: 'close_reason',
        headerName: 'Close Reason',
        minWidth: 150,
        flex: 0.8,
        renderCell: ({ value }) => (
          value ? (
            <Chip
              label={String(value).replace(/_/g, ' ').toUpperCase()}
              color={value === 'inactivity' ? 'warning' : 'default'}
              size="small"
              sx={{ fontWeight: 700 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )
        ),
      },
      {
        field: 'retention_state',
        headerName: 'Retention',
        minWidth: 150,
        flex: 0.8,
        sortable: false,
        renderCell: (params) => {
          const value = params?.row;
          if (!value) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          if (value.retention_hold) {
            return <Chip label="HOLD" color="error" size="small" sx={{ fontWeight: 700 }} />;
          }
          if (value.purged_at) {
            return <Chip label="PURGED" color="default" size="small" sx={{ fontWeight: 700 }} />;
          }
          if (value.is_archived) {
            return <Chip label="ARCHIVED" color="warning" size="small" sx={{ fontWeight: 700 }} />;
          }
          return <Chip label="ACTIVE" color="success" size="small" sx={{ fontWeight: 700 }} />;
        },
      },
      {
        field: 'support_mode',
        headerName: 'Support Mode',
        minWidth: 140,
        flex: 0.8,
        renderCell: ({ value }) => (
          <Chip
            label={formatSupportMode(value)}
            color={getSupportModeColor(value)}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        ),
      },
      {
        field: 'intent',
        headerName: 'Intent',
        minWidth: 150,
        flex: 0.8,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {value ? value.replace(/_/g, ' ') : '-'}
          </Typography>
        ),
      },
      {
        field: 'unread_notification_count',
        headerName: 'Unread',
        minWidth: 110,
        flex: 0.6,
        renderCell: ({ value }) => (
          value > 0 ? (
            <Chip
              label={`${value} unread`}
              color="error"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )
        ),
      },
      {
        field: 'assigned_agent',
        headerName: 'Assigned Agent',
        minWidth: 180,
        flex: 1,
        renderCell: ({ value }) => getDisplayName(value),
      },
      {
        field: 'created_at',
        headerName: 'Created At',
        minWidth: 160,
        flex: 0.9,
        renderCell: ({ value }) => formatDateTime(value),
      },
      {
        field: 'last_message_at',
        headerName: 'Last Message',
        minWidth: 160,
        flex: 0.9,
        renderCell: ({ value }) => formatDateTime(value),
      },
      {
        field: 'last_activity_at',
        headerName: 'Last Activity',
        minWidth: 160,
        flex: 0.9,
        renderCell: ({ value }) => formatDateTime(value),
      },
      {
        field: 'escalated_at',
        headerName: 'Escalated At',
        minWidth: 160,
        flex: 0.9,
        renderCell: ({ value }) => formatDateTime(value),
      },
      {
        field: 'auto_closed_at',
        headerName: 'Auto Closed',
        minWidth: 160,
        flex: 0.9,
        renderCell: ({ value }) => formatDateTime(value),
      },
      {
        field: 'escalation_reason',
        headerName: 'Escalation Reason',
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {value || '-'}
          </Typography>
        ),
      },
      {
        field: 'latest_message',
        headerName: 'Preview',
        minWidth: 260,
        flex: 1.5,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {value?.content || 'No messages yet'}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Tooltip title="Open conversation">
            <IconButton onClick={() => navigate(`/admin/support-chat/${row.id}`)} color="primary">
              <Visibility />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 9 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: (theme) => theme.palette.primary.main }}>
              Support Chat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor bot, waiting-agent, live-agent, and closed support sessions.
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Waiting chats: ${waitingChatCount}`} color="warning" size="small" />
              <Chip label={`Unread alerts: ${unreadNotificationCount}`} color="error" size="small" />
              <Chip
                label={socketConnected ? 'Live alerts connected' : 'REST fallback active'}
                color={socketConnected ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 12, md: 3 }}
          sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(soundAlertsEnabled)}
                  onChange={(event) => setSoundAlertsEnabled(event.target.checked)}
                  color="primary"
                />
              )}
              label="Sound on every new message"
            />
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>
              Off keeps only the first support-chat sound for this session.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchSessions();
                refreshUnreadCount();
              }}
            >
              Refresh
            </Button>
          </Box>
        </Grid>
      </Grid>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Live Chat Hours
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {availability?.working_hours_label || '9 AM to 6 PM Nigeria time'}
              </Typography>
              {availability ? (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={availability.is_available ? 'Agents visible now' : 'Agents hidden now'}
                    color={availability.is_available ? 'success' : 'warning'}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    label={availability.within_working_hours ? 'Within work hours' : 'Outside work hours'}
                    color={availability.within_working_hours ? 'info' : 'default'}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    label={availability.manually_available ? 'Manual status: available' : 'Manual status: unavailable'}
                    color={availability.manually_available ? 'success' : 'error'}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Turn manual availability off during breaks or temporary downtime so users are asked to leave a message.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Start time"
                type="time"
                value={availabilityDraft.start_time}
                onChange={(event) => setAvailabilityDraft((prev) => ({ ...prev, start_time: event.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="End time"
                type="time"
                value={availabilityDraft.end_time}
                onChange={(event) => setAvailabilityDraft((prev) => ({ ...prev, end_time: event.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(availabilityDraft.manually_available)}
                    onChange={(event) => setAvailabilityDraft((prev) => ({ ...prev, manually_available: event.target.checked }))}
                    color="primary"
                  />
                )}
                label="Available now"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(availabilityDraft.is_enabled)}
                    onChange={(event) => setAvailabilityDraft((prev) => ({ ...prev, is_enabled: event.target.checked }))}
                    color="primary"
                  />
                )}
                label="Enforce hours"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveAvailability}
                disabled={availabilityLoading || availabilitySaving}
              >
                {availabilitySaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Grid>
            {availabilityMessage ? (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity={availabilityMessage.includes('updated') ? 'success' : 'warning'}
                  onClose={() => setAvailabilityMessage('')}
                >
                  {availabilityMessage}
                </Alert>
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Search user"
                placeholder="Name, email, phone, or session ID"
                value={draftFilters.search}
                onChange={(event) => setDraftFilters((prev) => ({ ...prev, search: event.target.value }))}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {STATUS_OPTIONS.map((option) => (
                    <Chip
                      key={option.value || 'all'}
                      label={option.label}
                      clickable
                      color={draftFilters.status === option.value ? 'primary' : 'default'}
                      onClick={() => setDraftFilters((prev) => ({ ...prev, status: option.value }))}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {RETENTION_OPTIONS.map((option) => (
                    <Chip
                      key={option.value || 'retention_all'}
                      label={option.label}
                      clickable
                      color={draftFilters.retention_status === option.value ? 'primary' : 'default'}
                      onClick={() => setDraftFilters((prev) => ({ ...prev, retention_status: option.value }))}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {CLOSE_REASON_OPTIONS.map((option) => (
                    <Chip
                      key={option.value || 'close_reason_all'}
                      label={option.label}
                      clickable
                      color={draftFilters.close_reason === option.value ? 'primary' : 'default'}
                      onClick={() => setDraftFilters((prev) => ({ ...prev, close_reason: option.value }))}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                fullWidth
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  '&:hover': { backgroundColor: (theme) => theme.palette.primary.dark },
                }}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 520 }}>
              <DataGrid
                autoHeight
                rows={displaySessions}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                hideFooterPagination
                hideFooter
                disableRowSelectionOnClick
                onRowClick={(params) => navigate(`/admin/support-chat/${params.row.id}`)}
                sx={{
                  border: 'none',
                  minWidth: 1500,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: (theme) => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: '#fff',
                    fontWeight: 700,
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: '#fff',
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={pageSize}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
}
